<template>
	<div>
		<div class="flex justify-between items-center lg:py-5 py-2">
			<div class="flex gap-4">
				<LayoutSideMenu
					:menu="menu"
					:top-navbar-left="topNavbarLeft"
					:cultures-norce="culturesNorce"
					:currencies-norce="currenciesNorce"
				/>
				<button
					type="button"
					class="lg:hidden"
					@click="isSearchOpen = !isSearchOpen"
				>
					<MagnifyingGlassIcon class="h-6 w-6" />
					<span class="sr-only">{{ $t("Open search") }}</span>
				</button>
			</div>
			<div class="flex justify-center">
				<div class="flex justify-center items-center">
					<NuxtLink :to="localePath(`/`)">
						<NuxtImg
							v-show="!isTransparentHeader"
							:src="siteLogo?.filename"
							:alt="siteLogo?.alt || 'logo'"
							class="logo max-w-[125px]"
							loading="eager"
							:width="siteLogoDimensions?.width"
							:height="siteLogoDimensions?.height"
						/>
						<NuxtImg
							v-show="isTransparentHeader"
							:src="siteLogoWhite?.filename"
							:alt="siteLogo?.alt || 'logo'"
							class="logo max-w-[125px]"
							loading="eager"
							:width="siteLogoDimensions?.width"
							:height="siteLogoDimensions?.height"
						/>
					</NuxtLink>
				</div>
			</div>

			<div class="flex gap-4 items-center justify-end">
				<!-- Cart -->
				<div
					class="relative cursor-pointer"
					data-testid="cart-toggle"
					@click="isSidebarOpen = true"
				>
					<ShoppingBagIcon class="h-6 w-6 cursor-pointer" />
					<client-only>
						<span
							v-if="itemsCount > 0"
							data-testid="cart-badge"
							class="text-[10px] text-center absolute rounded-full w-5 h-5 leading-5 -top-2 -right-2"
							:class="
								isTransparentHeader
									? 'bg-tertiary text-primary'
									: 'bg-primary text-white'
							"
						>
							{{ itemsCount || "" }}
						</span>
					</client-only>
				</div>
				<UserIcon class="h-6 w-6" @click="openAccountOnDesktop" />
			</div>
		</div>
		<div
			v-show="isSearchOpen"
			class="absolute bottom-[-56px] -left-4 -right-4 py-2.5 px-4"
			:class="!isTransparentHeader ? 'bg-white' : 'transparent-search-input'"
		>
			<StoryblokComponent
				v-for="blok in search"
				:key="blok._uid"
				:blok="blok"
			/>
		</div>
		<LayoutSideCart />
	</div>
</template>

<script setup lang="ts">
import {
	MagnifyingGlassIcon,
	ShoppingBagIcon,
	UserIcon
} from "@heroicons/vue/24/outline";
import { provide, ref } from "vue";
import type { Currency } from "@nwt/norce-pwa/dist/runtime/types/api/application/application";
import type {
	CulturesNorce,
	SiteLogoDimensions
} from "~/types/components/header";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import type {
	ResolvedMenuNode,
	Search,
	SiteLogo,
	TopNavbarLeft
} from "~/types/storyblok/storyblok";
import { storeToRefs } from "pinia";

const cartStore = useCartStore();
const { userHasSession, getUserByEmailIfHasSession } = useLoadUser();
const localePath = useLocalePath();
const { getUser } = storeToRefs(userStore());

const itemsCount = computed(() => cartStore.itemsCount());

const isSidebarOpen = ref(false);

provide("isSidebarOpen", isSidebarOpen);

interface Props {
	topNavbarLeft: TopNavbarLeft[] | undefined;
	culturesNorce: CulturesNorce[] | undefined;
	currenciesNorce: Currency[] | undefined;
	search: Search[] | undefined;
	siteLogo: SiteLogo | undefined;
	siteLogoWhite: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	menu: ResolvedMenuNode[];
	isTransparentHeader?: boolean;
}

const {
	topNavbarLeft,
	culturesNorce,
	currenciesNorce,
	search,
	siteLogo,
	siteLogoDimensions,
	menu
} = defineProps<Props>();

const modal = inject<SharedModal>("modal") as SharedModal;

const isSearchOpen = ref(false);

const openAccountMenu = () =>
	modal.open("AccountMobileMenu", { position: "side-left" });

const openAccountOnDesktop = async () => {
	// First check if user exists in store (from localStorage persistence)
	if (getUser.value && Object.keys(getUser.value).length > 0) {
		openAccountMenu();
	} else if (!(await userHasSession())) {
		modal.open("AccountLoginForm", {
			position: "side-left"
		});
	} else {
		const account = await getUserByEmailIfHasSession();
		if (account) {
			openAccountMenu();
		} else {
			modal.open("AccountLoginForm", { position: "side-left" });
		}
	}
};
</script>
